import { Button, Note, Panel } from "@platform/ui";
import { ErrorText, errorKey, Field, SelectField, useSession } from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";

type PlanKey = "starter" | "growth" | "scale";
type Billing = "monthly" | "yearly";

interface PlanView {
  readonly plan: PlanKey;
  readonly billing: Billing | null;
  readonly currentPeriodEnd: string | null;
  readonly members: number;
  readonly enforced: boolean;
}

const PLAN_KEYS: readonly PlanKey[] = ["starter", "growth", "scale"];

/** Whether the operator handles billing, and so reads and sets enterprises' plans (ENT-7, card 120). */
function useBills(): boolean {
  const { state } = useSession();
  return state.status === "signed_in" && state.claims.perm.includes("ops:finance.invoice");
}

/** The last moment of a day in Singapore, the end of a paid period. */
const endOfDay = (date: string) => new Date(`${date}T23:59:59+08:00`).toISOString();
/** That day, in Singapore, for the date field. */
const dayOf = (iso: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date(iso));

/**
 * An enterprise's plan (ADR 0023, card 120), for operators who handle billing: Starter has no billing; a paid plan has
 * its billing period and the day it is paid until. Each change carries a reason on the audit trail.
 */
export function EnterprisePlanPanel({ enterpriseId }: { enterpriseId: string }) {
  const t = useTranslations();
  const bills = useBills();
  const { session } = useSession();
  const [view, setView] = useState<PlanView | undefined>();
  const [plan, setPlan] = useState<PlanKey>("starter");
  const [billing, setBilling] = useState<Billing>("yearly");
  const [periodEnd, setPeriodEnd] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);

  const show = useCallback((data: PlanView) => {
    setView(data);
    setPlan(data.plan);
    if (data.billing !== null) setBilling(data.billing);
    setPeriodEnd(data.currentPeriodEnd === null ? "" : dayOf(data.currentPeriodEnd));
  }, []);

  useEffect(() => {
    if (!bills) return;
    void session.api
      .GET("/v1/ops/enterprises/{enterpriseId}/plan", { params: { path: { enterpriseId } } })
      .then(({ data, error: problem }) => (data === undefined ? setError(t(errorKey(problem))) : show(data)));
  }, [bills, session, enterpriseId, show, t]);

  if (!bills) return null;
  if (view === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const free = plan === "starter";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSaved(false);
    const { data, error: problem } = await session.api.PUT("/v1/ops/enterprises/{enterpriseId}/plan", {
      params: { path: { enterpriseId } },
      body: {
        plan,
        billing: free ? null : billing,
        currentPeriodEnd: free ? null : endOfDay(periodEnd),
        reason: reason.trim(),
      },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    show(data);
    setReason("");
    setSaved(true);
  };

  return (
    <Panel
      title={t("admin.plan.title")}
      description={t("admin.plan.current", { plan: t(`plan.names.${view.plan}`), members: view.members })}
    >
      <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
        <SelectField
          id="plan-key"
          label={t("admin.plan.plan")}
          value={plan}
          onChange={(value) => setPlan(value as PlanKey)}
          options={PLAN_KEYS.map((key) => ({ value: key, label: t(`plan.names.${key}`) }))}
        />
        {!free && (
          <>
            <SelectField
              id="plan-billing"
              label={t("admin.plan.billing")}
              value={billing}
              onChange={(value) => setBilling(value as Billing)}
              options={[
                { value: "monthly", label: t("admin.plan.monthly") },
                { value: "yearly", label: t("admin.plan.yearly") },
              ]}
            />
            <Field
              id="plan-period-end"
              label={t("admin.plan.periodEnd")}
              type="date"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
              required
            />
          </>
        )}
        <Field
          id="plan-reason"
          label={t("admin.plan.reason")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          required
        />
        {!view.enforced && <Note>{t("admin.plan.pilot")}</Note>}
        {saved && (
          <Note tone="done" role="status">
            {t("admin.plan.saved")}
          </Note>
        )}
        {error !== undefined && <ErrorText>{error}</ErrorText>}
        <Button
          type="submit"
          variant="secondary"
          disabled={reason.trim().length < 3 || (!free && periodEnd === "")}
        >
          {t("admin.plan.save")}
        </Button>
      </form>
    </Panel>
  );
}
